(ns ycweb.app.admin.tech.services.cronscheduler.overview
  (:require
   #?@(:clj
       [[clj-time.core :as dt]
        [clj-time.coerce :as dtc]]
       :cljs
       [[cljs-time.core :as dt]
        [cljs-time.coerce :as dtc]])
   [clojure.string :as str]
   [rum.core :as rum]
   [ycweb.app.dashboard.common :as dcommon]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.editor.base :as editor]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.date :as ud]
   [ycweb.util.urlstate :as urlstate]))

;; ============================================================================
;; Shared Utility Functions
;; ============================================================================

(defn row-colour [status]
  (cond
    ;; Manual executions - light blue
    (and status (str/starts-with? status "Manual: ")) "#add8e6"
    (contains? #{"Missed" "Exception"} status) "#f18d8b"
    (contains? #{"Cancelled"} status) "#f39b5d"
    (contains? #{"Misfire" "Skipped"} status) "#ffda6a"
    :else "white"))

(defn format-date-or-na [value fmt]
  (if value
    (ud/format-date value fmt)
    "N/A"))

(defn consumer-status-colour [status]
  (cond
    (= status "Exception") "#f18d8b"   ;; Red (should rarely happen now)
    (= status "Unknown") "#ff6b6b"     ;; Bright red - exception was swallowed
    (= status "Unhandled") "#ffa500"   ;; Dark orange - empty map returned
    (= status "Failed") "#f39b5d"      ;; Orange - explicit failure from handler
    (= status "Timeout") "#f39b5d"     ;; Orange
    (= status "Running") "#87ceeb"     ;; Sky blue
    (= status "Finished") "#90EE90"    ;; Green
    :else "white"))

(rum/defc table-row-consumer-log [log]
  [:tr
   {:style {:background-color (consumer-status-colour (:status log))}}
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (or (:sequence-number log) "-")]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "left"}}
    (:entity-identifier log)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (:status log)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (cond
      (:duration-seconds log)
      (str (:duration-seconds log) "s")
      :else "-")]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "left"
                 :max-width "200px" :overflow "hidden" :text-overflow "ellipsis"}}
    (:message log)]])

(defn trend-row-colour [trend-row]
  (cond
    (> (:exception-count trend-row) 0) "#f18d8b"
    (> (:misfire-count trend-row) 0) "#ffda6a"
    :else "white"))

(rum/defc table-row-trend [trend-row]
  [:tr
   {:style {:background-color (trend-row-colour trend-row)}}
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (ud/format-date (:date-from trend-row) "dd/MM")]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (:expected-count trend-row)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (:actual-count trend-row)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (:finished-count trend-row)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"
                 :color (if (> (:exception-count trend-row) 0) "#c0392b" "inherit")
                 :font-weight (if (> (:exception-count trend-row) 0) "bold" "normal")}}
    (:exception-count trend-row)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"
                 :color (if (> (:misfire-count trend-row) 0) "#d35400" "inherit")
                 :font-weight (if (> (:misfire-count trend-row) 0) "bold" "normal")}}
    (:misfire-count trend-row)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (cond
      (:avg-time-seconds trend-row)
      (str (.toFixed (:avg-time-seconds trend-row) 1) "s")
      :else "N/A")]])

(defn cron-summary-data [logs]
  (let [total-count (count logs)
        [missed-logs actual-logs] (u/separate #(= (:status %) "Missed") logs)
        sorted-missed-logs (sort-by :datescheduled missed-logs)
        missing-count (count missed-logs)
        actual-count (count actual-logs)
        started-count (count (filter #(u/presence (:datestarted %)) actual-logs))
        finished-logs (filter #(and (u/presence (:datestarted %))
                                 (u/presence (:datefinished %))) actual-logs)
        finished-count (count finished-logs)
        timed-logs
        (map
          (fn [finished-log]
            (let [seconds
                  (dt/in-seconds
                    (dt/interval
                      (:datestarted finished-log)
                      (:datefinished finished-log)))]
              (assoc finished-log :seconds-taken seconds)))
          finished-logs)
        times (seq (map :seconds-taken timed-logs))
        max-time (when times (apply max times))
        min-time (when times (apply min times))
        avg-time (when times (u/avg times))
        nomsgfin-count
        (count
          (filter
            (fn [log]
              (and
                (= (:status log) "Finished")
                (= (:message log) "No Message")))
            actual-logs))
        unfinished-count (- actual-count finished-count)
        cancelled-count (count (filter #(= (:status %) "Cancelled") actual-logs))
        cancelled-perc (if (pos? actual-count) (int (* (/ cancelled-count actual-count) 100)) 0)
        skipped-count (count (filter #(= (:status %) "Skipped") actual-logs))
        skipped-perc (if (pos? actual-count) (int (* (/ skipped-count actual-count) 100)) 0)
        misfire-count (count (filter #(= (:status %) "Misfire") actual-logs))
        misfire-perc (if (pos? actual-count) (int (* (/ misfire-count actual-count) 100)) 0)
        exception-count (count (filter #(= (:status %) "Exception") actual-logs))
        exception-perc (if (pos? actual-count) (int (* (/ exception-count actual-count) 100)) 0)
        network-failed-count
        (count
          (filter
            (fn [log]
              (and
                (= (:status log) "Exception")
                (u/presence (:message log))
                (or
                  (str/includes? (:message log) "Network is unreachable")
                  (str/includes? (:message log) "Read timed out"))))
            actual-logs))
        network-failed-countperc (if (pos? total-count) (int (* (/ network-failed-count total-count) 100)) 0)
        missing-perc (if (pos? total-count) (int (* (/ missing-count total-count) 100)) 0)
        first-missing-date (:datescheduled (first sorted-missed-logs))
        first-missing (if first-missing-date (ud/format-date first-missing-date "dd/MM HH:mm") "N/A")
        last-missing-date (:datescheduled (last sorted-missed-logs))
        last-missing (if last-missing-date (ud/format-date last-missing-date "dd/MM HH:mm") "N/A")]
    {:expected-count total-count
     :actual-count actual-count
     :started-count started-count
     :finished-count finished-count
     :unfinished-count unfinished-count
     :missing-count missing-count
     :missing-perc missing-perc
     :cancelled-count cancelled-count
     :cancelled-perc cancelled-perc
     :skipped-count skipped-count
     :skipped-perc skipped-perc
     :misfire-count misfire-count
     :misfire-perc misfire-perc
     :exception-count exception-count
     :exception-perc exception-perc
     :network-failed-count network-failed-count
     :network-failed-countperc network-failed-countperc
     :max-time max-time
     :min-time min-time
     :avg-time avg-time
     :first-missing first-missing
     :last-missing last-missing}))

(defn reconcile-cron-firings [expected-firings cron-logs]
  (let [idx-cron-logs (u/index-by :datescheduled cron-logs)]
    (reduce
      (fn [acc expected-firing]
        (let [expected-scheduled-time (:scheduled-time expected-firing)
              expected-unixtime (ud/php-timestamp expected-scheduled-time)
              next-fire-time (:next-scheduled-time expected-firing)
              next-fire-unixtime (ud/php-timestamp next-fire-time)
              log (get idx-cron-logs expected-scheduled-time)
              misfire-log
              (filter
                (fn [cron-log]
                  (let [datescheduled-unixtime (ud/php-timestamp (:datescheduled cron-log))]
                    (and
                      (> datescheduled-unixtime expected-unixtime)
                      (< datescheduled-unixtime next-fire-unixtime))))
                (vals idx-cron-logs))]
          (conj acc
            (cond
              (and
                (nil? log)
                (empty? misfire-log))
              {:status "Missed"
               :message ""
               :datestarted nil
               :datefinished nil
               :datescheduled expected-scheduled-time
               :datedscheduled-unixtime expected-unixtime}

              (not (empty? misfire-log))
              (assoc
                (first misfire-log)
                :status "Misfire"
                :datedscheduled-unixtime expected-unixtime)

              :else
              (assoc log
                :datedscheduled-unixtime expected-unixtime)))))
      [] expected-firings)))

(defn cron-log-metadata [{:keys [expected-firings cron-logs]}]
  (let [now (dt/now)
        now-unixtime (ud/php-timestamp now)
        base-logs
        (cond
          (not (empty? expected-firings))
          (reconcile-cron-firings expected-firings cron-logs)

          :else
          (map
            (fn [log]
              (assoc log :datedscheduled-unixtime (ud/php-timestamp (:datescheduled log))))
            cron-logs))
        current-logs
        (remove
          (fn [cron-log]
            (> (:datedscheduled-unixtime cron-log) now-unixtime))
          base-logs)
        logs (sort-by :datedscheduled-unixtime u/desc current-logs)
        summary (cron-summary-data current-logs)]
    {:cron/summary summary
     :cron/logs logs
     :cron/scheduled-to now}))

;; ============================================================================
;; Data Receive Handler
;; ============================================================================

(defmethod comms/receive :admin.cronscheduler/overview [app-atom response]
  (let [process-atom (cmn/process-cursor app-atom)
        {:keys [cron trend job-config]} response
        cron-health
        (assoc (cron-log-metadata cron)
          :cron/scheduled-from (:scheduled-from cron))]
    (toaster/close-overlay app-atom :admin.cronscheduler/cron-overview)
    (swap! process-atom assoc
      :cron-overview cron-health
      :cron-trend (:data trend)
      :job-config job-config
      :job-name (:job-name response)
      :logs-page 0)))

;; ============================================================================
;; Status Badge
;; ============================================================================

(defn health-badge [summary]
  (let [exception-count (or (:exception-count summary) 0)
        missing-count (or (:missing-count summary) 0)
        status-color
        (cond
          (> exception-count 0) "#f18d8b"
          (> missing-count 5) "#ffda6a"
          :else "#90EE90")
        status-text
        (cond
          (> exception-count 0) "Exceptions"
          (> missing-count 5) "Missing"
          :else "Healthy")]
    [:span
     {:style {:background-color status-color
              :padding "4px 12px"
              :border-radius "4px"
              :font-size "14px"
              :font-weight "bold"}}
     status-text]))

;; ============================================================================
;; Header Component
;; ============================================================================

(rum/defc overview-header < rum/reactive cmn/cursors [app-atom job-name job-config]
  [:div.u-margin-bottom-30
   {:class (u/bem :flex-grid-group [:valign-middle :align-left :flush-force])}
   [:div
    {:class (u/bem :flex-grid-block [:fluid])}
    [:div.flex-grid-content
     (headings/heading job-name
       {:type :h1
        :size :large
        :flush true})]]
   [:div
    {:class (u/bem :flex-grid-block [:align-right :fluid])}
    [:div.flex-grid-content
     [:div
      {:class (u/bem :flex-grid-group [:valign-middle :align-left :flush-force])}
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Execute"
          {:style :primary
           :icon :play
           :on-click #(grid/dispatch-action app-atom :admin.cronscheduler/open-execute-modal
                        {:cron/job-name job-name
                         :cron/job-group (or (:job-group job-config) "DEFAULT")})})]]
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Back"
          {:style :secondary
           :icon :arrow-left
           :on-click #(urlstate/back)})]]]]]])

;; ============================================================================
;; Job Configuration Content (for accordion)
;; ============================================================================

(rum/defc job-config-content < rum/reactive cmn/cursors [job-config]
  (when job-config
    [:div.u-margin-bottom-20
     [:div
      {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])}
      [:div
       {:class (u/bem :flex-grid-block [:ml-fluid])}
       [:div.flex-grid-content
        [:span {:style {:color "#555" :font-size "12px" :text-transform "uppercase" :font-weight "bold"}}
         "Job Configuration"]]]]
     [:div
      {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])}
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Trigger: "]
        (or (:trigger-name job-config) "N/A") "/" (or (:trigger-group job-config) "N/A")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Expression: "]
        (or (:cron-expression job-config) "N/A")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Status: "]
        [:span {:style {:background-color (if (= (:job-status job-config) "Active") "#90EE90" "#ccc")
                        :padding "2px 8px"
                        :border-radius "4px"
                        :font-size "12px"}}
         (or (:job-status job-config) "Unknown")]]]]
     [:div
      {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])}
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Next Fire: "]
        (if (:next-fire-time job-config)
          (ud/format-date (:next-fire-time job-config) "dd MMM yyyy HH:mm:ss")
          "N/A")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Prev Fire: "]
        (if (:prev-fire-time job-config)
          (ud/format-date (:prev-fire-time job-config) "dd MMM yyyy HH:mm:ss")
          "N/A")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Priority: "]
        (or (:priority job-config) "N/A")]]]]))

;; ============================================================================
;; Description Section
;; ============================================================================

(defmethod comms/receive :admin.cronscheduler/overview.update.description [app-atom {:keys [success description]}]
  (toaster/close-overlay app-atom :admin.cronscheduler/update-description)
  (when success
    (grid/close-modal app-atom)
    ;; Update local state directly to avoid replication lag from slave db
    (let [process-atom (cmn/process-cursor app-atom)]
      (swap! process-atom assoc-in [:job-config :description] description))))

(defmethod grid/action :admin.cronscheduler/save-overview-description [app-atom {:cron/keys [job-name]} _]
  (let [modal-atom (grid/modal-cursor app-atom)
        process-atom (cmn/process-cursor app-atom)
        description (cmn/curact modal-atom [:config :description])
        cron-job-id (cmn/curact process-atom [:job-config :cron-job-id])]
    (toaster/open-overlay app-atom :admin.cronscheduler/update-description)
    (comms/send :admin.cronscheduler/overview.update.description
      {:cron-job-id cron-job-id
       :description (or description "")})))

(rum/defc description-edit-modal-body < rum/reactive cmn/cursors [app-atom modal-atom _]
  (let [job-name (cmn/curact modal-atom :cron/job-name)
        description-atom (rum/cursor modal-atom :description)]
    [:div
     (headings/heading "Edit Description"
       {:type :h4
        :size :medium
        :subtext job-name})
     [:div {:style {:margin-top "16px"}}
      (headings/heading "Description"
        {:type :h5
         :size :tiny})
      (forms/textarea-raw description-atom
        {:placeholder "Enter a description to help developers understand what this cron job does..."
         :rows 5})]
     (toaster/loading-overlay app-atom :admin.cronscheduler/update-description)]))

(rum/defc description-edit-modal-footer [app-atom modal-atom _]
  (let [job-name (cmn/curact modal-atom :cron/job-name)]
    (grid/button-row
      {:left-text "Cancel"
       :left-no-icon true
       :left-fn #(grid/close-modal app-atom)
       :right-text "Save"
       :right-no-icon true
       :right-fn
       #(grid/dispatch-modal-action app-atom :admin.cronscheduler/save-overview-description
          {:cron/job-name job-name} {:prevent-closing true})})))

(defmethod grid/modal-config :admin.cronscheduler/overview-edit-description [_ _ _]
  {:body description-edit-modal-body
   :footer description-edit-modal-footer
   :cancel-key :admin.cronscheduler/close-overview-description
   :layout :modal
   :modal-type :details
   :max-width 600})

(defmethod grid/action :admin.cronscheduler/open-overview-description [app-atom {:cron/keys [job-name description]} _]
  (grid/open-modal app-atom :admin.cronscheduler/overview-edit-description
    {:cron/job-name job-name
     :description (or description "")}))

(rum/defc description-content < rum/reactive cmn/cursors [app-atom job-name job-config]
  [:div.u-margin-bottom-20
   [:div
    {:class (u/bem :flex-grid-group [:valign-middle :align-left :flush])}
    [:div
     {:class (u/bem :flex-grid-block [:fluid])}
     [:div.flex-grid-content
      (headings/heading "Description"
        {:type :h5
         :size :tiny})]]
    [:div
     {:class (u/bem :flex-grid-block [:align-right :fluid])}
     [:div.flex-grid-content
      (buttons/button-web "Edit"
        {:style :secondary
         :icon :pencil
         :size :small
         :on-click #(grid/dispatch-action app-atom :admin.cronscheduler/open-overview-description
                      {:cron/job-name job-name
                       :cron/description (:description job-config)})})]]]
   [:div {:style {:padding "8px 0" :color (if (:description job-config) "inherit" "#999")}}
    (or (:description job-config) "No description. Click Edit to add one.")]])

;; ============================================================================
;; Consolidated Stats Card
;; ============================================================================

(defn- format-time-or-na [value]
  (cond
    (nil? value) "N/A"
    (zero? value) "<1"
    :else value))

(rum/defc stats-content < rum/reactive cmn/cursors [app-atom summary scheduled-from scheduled-to]
  [:div
   ;; Header row with status and period
   [:div
    {:style {:padding "6px 0px"}
     :class (u/bem :flex-grid-group [:valign-middle :flush])}
    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Status: "]
      [:i
       {:class (u/bem :icon [:info :inline :is-dark])
        :on-click
        #(grid/message-modal app-atom
           {:title "Status Explanation"
            :text "Overall health status for the period based on the most significant issue. Shows 'Exceptions' if any exceptions occurred, 'Missing' if more than 5 expected executions were missed, otherwise 'Healthy'."
            :confirm-text "OK"})
        :style {:font-size "14px" :cursor "pointer" :vertical-align "middle" :margin-right "6px"}}]
      (health-badge summary)]]

    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Period: "]
      (ud/format-date scheduled-from "d MMM HH:mm")
      " - "
      (ud/format-date scheduled-to "d MMM HH:mm")]]]

   [:hr]

   ;; Summary section
   [:div {:style {:padding "6px 0px"}}
    [:div.flex-grid-content
     (headings/heading "Summary"
       {:type :h5
        :size :tiny})]
    [:div
     {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])}
     [:div
      {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
      [:div.flex-grid-content
       [:b "Expected: "] (:expected-count summary)]]
     [:div
      {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
      [:div.flex-grid-content
       [:b "Actual: "] (:actual-count summary)]]
     [:div
      {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
      [:div.flex-grid-content
       [:b "Missing: "] (:missing-count summary) (str " (" (:missing-perc summary) "%)")]]]]

   [:hr]

   [:div {:style {:padding "6px 0px"}}
    ;; Execution Statistics section
    [:div
     {:style {:padding "6px 0px"}}
     (headings/heading "Execution Statistics"
       {:type :h5
        :size :tiny})

    ;; Status counts row (Cancelled, Skipped, Misfire, Exception)
     [:div
      {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])
       :style {:margin-bottom "4px"}}
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Cancelled: "] (:cancelled-count summary) (str " (" (:cancelled-perc summary) "%)")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Skipped: "] (:skipped-count summary) (str " (" (:skipped-perc summary) "%)")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Misfire: "] (:misfire-count summary) (str " (" (:misfire-perc summary) "%)")]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Exception: "] (:exception-count summary) (str " (" (:exception-perc summary) "%)")]]]
       ;; Started/Finished/Unfinished row
     [:div
      {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])
       :style {:margin-bottom "4px"}}
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Started: "] (:started-count summary)]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Finished: "] (:finished-count summary)]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Unfinished: "] (:unfinished-count summary)]]]
       ;; Timing stats row (only from finished crons)
     [:div
      {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])}
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Max time (s): "] (format-time-or-na (:max-time summary))]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Min time (s): "] (format-time-or-na (:min-time summary))]]
      [:div
       {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
       [:div.flex-grid-content
        [:b "Avg time (s): "] (format-time-or-na (:avg-time summary))]]]]]

   [:hr]

   [:div {:style {:padding "6px 0px"}}
    ;; Missing Summary section
    (headings/heading "Missing Summary"
      {:type :h5
       :size :tiny})
    [:div
     {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])}
     [:div
      {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
      [:div.flex-grid-content
       [:b "Network Failed: "] (:network-failed-count summary) (str " (" (:network-failed-countperc summary) "%)")]]
     [:div
      {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
      [:div.flex-grid-content
       [:b "First Missing: "] (:first-missing summary)]]
     [:div
      {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
      [:div.flex-grid-content
       [:b "Last Missing: "] (:last-missing summary)]]]]])

;; ============================================================================
;; Consumer Logs Modal
;; ============================================================================

(rum/defc consumer-logs-modal-body < rum/reactive cmn/cursors [app-atom modal-atom _]
  (let [process-atom (cmn/process-cursor app-atom)
        consumer-logs (cmn/curact process-atom :consumer-logs)
        scheduled-date (cmn/curact modal-atom :scheduled-date)
        {:keys [logs summary total]} consumer-logs
        finished-count (get-in summary ["Finished" :count] 0)
        exception-count (get-in summary ["Exception" :count] 0)
        timeout-count (get-in summary ["Timeout" :count] 0)
        running-count (get-in summary ["Running" :count] 0)]
    [:div
     (headings/heading "Consumer Logs"
       {:type :h4
        :size :small
        :flush true
        :subtext (str "Execution: " scheduled-date)})
     (cond
       consumer-logs
       [:div
        [:div {:style {:margin-bottom "8px" :font-size "13px"}}
         [:span {:style {:margin-right "16px"}}
          [:b "Total: "] total]
         [:span {:style {:margin-right "16px" :color "#27ae60"}}
          [:b "Finished: "] finished-count]
         (when (> exception-count 0)
           [:span {:style {:margin-right "16px" :color "#c0392b"}}
            [:b "Exceptions: "] exception-count])
         (when (> timeout-count 0)
           [:span {:style {:margin-right "16px" :color "#d35400"}}
            [:b "Timeouts: "] timeout-count])
         (when (> running-count 0)
           [:span {:style {:margin-right "16px" :color "#2980b9"}}
            [:b "Running: "] running-count])]
        (cond
          (and logs (not (empty? logs)))
          [:table {:class (u/bem :oTable [:has-shadow])
                   :style {:font-size "12px"}}
           [:thead
            [:tr
             [:th.u-text-center {:style {:width "40px"}} "#"]
             [:th.u-text-left "Entity"]
             [:th.u-text-center "Status"]
             [:th.u-text-center "Duration"]
             [:th.u-text-left "Message"]]]
           (into [:tbody]
             (->> logs
               (map table-row-consumer-log)))]

          :else
          [:div {:style {:padding "12px" :color "#666"}}
           "No consumer logs for this execution."])]

       :else
       [:div {:style {:padding "12px" :color "#666"}}
        "Loading consumer logs..."])
     (toaster/loading-overlay app-atom :admin.cronscheduler/consumer-logs)]))

(defmethod grid/modal-config :admin.cronscheduler/consumer-logs-modal [_ _ _]
  {:body consumer-logs-modal-body
   :cancel-key :admin.cronscheduler/close-consumer-logs
   :layout :modal
   :modal-type :alert
   :max-width 800})

(defmethod grid/action :admin.cronscheduler/open-consumer-logs [app-atom {:keys [cron-log-uuid scheduled-date]} _]
  (let [process-atom (cmn/process-cursor app-atom)]
    ;; Clear previous consumer logs
    (swap! process-atom dissoc :consumer-logs)
    (toaster/open-overlay app-atom :admin.cronscheduler/consumer-logs)
    (grid/open-modal app-atom :admin.cronscheduler/consumer-logs-modal
      {:cron-log-uuid cron-log-uuid :scheduled-date scheduled-date})
    (comms/send :admin.cronscheduler/consumer.logs {:cron-log-uuid cron-log-uuid})))

(defmethod grid/action :admin.cronscheduler/close-consumer-logs [app-atom _ _]
  (grid/close-modal app-atom))

(defmethod grid/action :admin.cronscheduler/refresh-overview [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        screen-params (cmn/screen-params app-atom)
        job-name (:job-name screen-params)]
    ;; Reset pagination and show loading
    (swap! process-atom assoc :logs-page 0)
    (toaster/open-overlay app-atom :admin.cronscheduler/cron-overview)
    (comms/send :admin.cronscheduler/overview
      {:cron/job-name job-name})))

;; ============================================================================
;; Logs Table with Pagination
;; ============================================================================

(def logs-per-page 10)

(defn log-row [app-atom row]
  [:tr
   {:style {:background-color (row-colour (or (:display-status row) (:status row)))}}
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "center"}}
    (format-date-or-na (:datescheduled row) "dd/MM/yyyy HH:mm:ss")]
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "center"}}
    (format-date-or-na (:datestarted row) "dd HH:mm:ss")]
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "center"}}
    (format-date-or-na (:datefinished row) "dd HH:mm:ss")]
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "center"}}
    (or (:display-status row) (:status row))]
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "left"}}
    (or (:user_email row) "-")]
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "left"}}
    (:message row)]
   [:td
    {:style {:padding "10px 6px" :border "1px solid #000" :text-align "center"}}
    (if (and (:uuid row) (> (:consumer_count row) 0))
      [:a {:style {:cursor "pointer" :color "#2980b9" :text-decoration "underline"}
           :on-click #(grid/dispatch-action app-atom :admin.cronscheduler/open-consumer-logs
                        {:cron-log-uuid (:uuid row)
                         :scheduled-date (format-date-or-na (:datescheduled row) "dd/MM/yyyy HH:mm:ss")})}
       "View"]
      "N/A")]])

(rum/defc logs-section < rum/reactive cmn/cursors [app-atom logs]
  (let [process-atom (cmn/process-cursor app-atom)
        current-page (or (cmn/curact process-atom :logs-page) 0)
        total-logs (count logs)
        total-pages (max 1 (int (Math/ceil (/ total-logs logs-per-page))))
        start-idx (* current-page logs-per-page)
        page-logs (->> logs
                    (drop start-idx)
                    (take logs-per-page))]
    [:div
     [:div
      {:class (u/bem :flex-grid-group [:valign-middle :flush])
       :style {:margin-bottom "12px"}}
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (headings/heading "Execution Logs" {:type :h4 :size :small :flush true})]]
      [:div
       {:class (u/bem :flex-grid-block [:align-right :fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Refresh"
          {:style :secondary
           :icon :refresh
           :on-click #(grid/dispatch-action app-atom :admin.cronscheduler/refresh-overview {})})]]]
     [:table {:class (u/bem :oTable [:has-shadow])}
      [:thead
       [:tr
        [:th.u-text-center "Date Scheduled"]
        [:th.u-text-center "Time Started"]
        [:th.u-text-center "Time Finished"]
        [:th.u-text-center "Status"]
        [:th.u-text-center "User Email"]
        [:th.u-text-center "Message"]
        [:th.u-text-center "Consumers"]]]
      (into
        [:tbody]
        (map #(log-row app-atom %) page-logs))]
     ;; Pagination controls
     (when (> total-pages 1)
       [:div {:style {:display "flex"
                      :justify-content "space-between"
                      :align-items "center"
                      :margin-top "12px"
                      :padding "8px 0"}}
        [:span {:style {:color "#666" :font-size "13px"}}
         (str "Showing " (inc start-idx) "-" (min (+ start-idx logs-per-page) total-logs)
           " of " total-logs " logs")]
        [:div {:style {:display "flex" :gap "8px"}}
         [:button
          {:class (u/bem :button [:small :secondary])
           :disabled (= current-page 0)
           :on-click #(swap! process-atom update :logs-page dec)}
          "Previous"]
         [:span {:style {:padding "6px 12px" :font-size "13px"}}
          (str "Page " (inc current-page) " of " total-pages)]
         [:button
          {:class (u/bem :button [:small :secondary])
           :disabled (>= (inc current-page) total-pages)
           :on-click #(swap! process-atom update :logs-page inc)}
          "Next"]]])]))

;; ============================================================================
;; Trend Table
;; ============================================================================

(rum/defc trend-section < rum/reactive cmn/cursors [trend-data]
  [:div
   (headings/heading "30-Day Trend" {:type :h4 :size :small :flush true})
   (cond
     (and trend-data (not (empty? trend-data)))
     [:table {:class (u/bem :oTable [:has-shadow])
              :style {:font-size "12px"}}
      [:thead
       [:tr
        [:th.u-text-center "Date"]
        [:th.u-text-center "Expected"]
        [:th.u-text-center "Actual"]
        [:th.u-text-center "Finished"]
        [:th.u-text-center "Exceptions"]
        [:th.u-text-center "Misfires"]
        [:th.u-text-center "Avg Time"]]]
      (into
        [:tbody]
        (->> trend-data
          (take 14)
          (map table-row-trend)))]

     :else
     [:div {:style {:padding "12px" :color "#666"}}
      "No trend data available yet. Trend data is aggregated daily."])])

;; ============================================================================
;; Execute Modal
;; ============================================================================

(defmethod comms/receive :admin.cronscheduler/cron.execute [app-atom {:keys [cron]}]
  (toaster/close-overlay app-atom :admin.cronscheduler/cron-execution)
  (let [process-atom (cmn/process-cursor app-atom)
        response (:cron/job-response cron)
        log-entry (:cron/log-entry cron)]
    (swap! process-atom assoc :execute-response response)
    ;; If we have a log entry, prepend it to the existing logs
    (when log-entry
      (swap! process-atom update-in [:cron-overview :cron/logs]
        (fn [existing-logs]
          (cons log-entry (or existing-logs [])))))))

(defmethod comms/receive :admin.cronscheduler/cron.job-data [app-atom {:keys [cron]}]
  (let [editor-atom (editor/editor-cursor app-atom :cron-executor)]
    (toaster/close-overlay app-atom :admin.cronscheduler/cron-job)
    (swap! editor-atom dissoc :form)
    (grid/open-modal app-atom :admin.cronscheduler/overview-execute
      {:cron/job-name (:cron/job-name cron)
       :cron/job-group (:cron/job-group cron)
       :cron/inputs (:cron/inputs cron)})))

(defmethod grid/action :admin.cronscheduler/open-execute-modal [app-atom {:cron/keys [job-name job-group]} _]
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom dissoc :execute-response)
    (toaster/open-overlay app-atom :admin.cronscheduler/cron-job)
    (comms/send :admin.cronscheduler/cron.job-data
      {:cron/job-name job-name
       :cron/job-group job-group})))

(defmethod grid/action :admin.cronscheduler/run-execute [app-atom {:cron/keys [job-name job-group]} _]
  (let [editor-atom (editor/editor-cursor app-atom :cron-executor)
        form (cmn/curact editor-atom :form)
        inputs (reduce (fn [acc [k v]] (assoc acc k (:value v))) {} form)]
    (toaster/open-overlay app-atom :admin.cronscheduler/cron-execution)
    (comms/send :admin.cronscheduler/cron.execute
      {:cron/job-name job-name
       :cron/job-group job-group
       :cron/inputs inputs})))

(rum/defc execute-modal-body < rum/reactive cmn/cursors [app-atom modal-atom _]
  (let [process-atom (cmn/process-cursor app-atom)
        job-name (cmn/curact modal-atom :cron/job-name)
        cron-inputs (cmn/curact modal-atom :cron/inputs)
        execute-response (cmn/curact process-atom :execute-response)
        editor-fields (map (fn [[k v]] {:name k :label (name k) :type v}) cron-inputs)]
    [:div
     (headings/heading (str "Execute: " job-name)
       {:type :h4 :size :medium})
     [:div
      (feedback/feedback :warning "Warning"
        [:ul
         [:li "This will execute the cron job on the CRON profile via an RPC call"]
         [:li "If the job has input parameters, they will appear below"]])
      (when (seq editor-fields)
        [:div
         (headings/heading "Inputs" {:type :h6 :size :small})
         (into [:form.u-max-width-480.u-block-center]
           (map (fn [field]
                  (editor/build-input-field app-atom {:editor-name :cron-executor} field))
             editor-fields))])
      (when execute-response
        [:div {:style {:margin-top "16px"}}
         (headings/heading "Response" {:type :h6 :size :small})
         (cond
           (:success execute-response)
           (feedback/feedback :success "Job Executed Successfully"
             [[:div [:b "Message: "] (:msg execute-response)]
              (when (:datadog/link execute-response)
                [:div
                 [:a {:href (:datadog/link execute-response)
                      :target "_blank"
                      :class (u/bem :button [:text-link :text-default])}
                  "View logs on Datadog"]])])

           :else
           (feedback/feedback :error "Job Failed"
             [[:div [:b "Message: "] (or (:msg execute-response) "Unknown error")]
              (when (:datadog/link execute-response)
                [:div
                 [:a {:href (:datadog/link execute-response)
                      :target "_blank"
                      :class (u/bem :button [:text-link :text-default])}
                  "View stack trace on Datadog"]])]))])]
     (toaster/loading-overlay app-atom :admin.cronscheduler/cron-job)
     (toaster/loading-overlay app-atom :admin.cronscheduler/cron-execution)]))

(rum/defc execute-modal-footer [app-atom modal-atom _]
  (let [job-name (cmn/curact modal-atom :cron/job-name)
        job-group (cmn/curact modal-atom :cron/job-group)]
    (grid/button-row
      {:left-text "Close"
       :left-no-icon true
       :left-fn #(grid/close-modal app-atom)
       :right-text "Execute"
       :right-no-icon true
       :right-fn #(grid/dispatch-modal-action app-atom :admin.cronscheduler/run-execute
                    {:cron/job-name job-name :cron/job-group job-group}
                    {:prevent-closing true})})))

(defmethod grid/modal-config :admin.cronscheduler/overview-execute [_ _ _]
  {:body execute-modal-body
   :footer execute-modal-footer
   :cancel-key :admin.cronscheduler/close-execute
   :layout :modal
   :modal-type :details
   :max-width 700})

;; ============================================================================
;; Main Overview Component
;; ============================================================================

(rum/defc cron-job-overview < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        cron-overview (cmn/curact process-atom :cron-overview)
        trend-data (cmn/curact process-atom :cron-trend)
        job-config (cmn/curact process-atom :job-config)
        job-name (cmn/curact process-atom :job-name)
        details-open? (cmn/curact process-atom :details-open?)
        {:cron/keys [summary logs scheduled-from scheduled-to]} cron-overview]
    [:div
     (overview-header app-atom job-name job-config)

     ;; Collapsible Job Details accordion
     [:div.u-margin-bottom-30
      {:class (u/bem :accordion [:card])}
      [:h3.accordion__heading
       [:a
        {:on-click #(swap! process-atom update :details-open? not)
         :class (u/bem :accordion__heading-link [(when details-open? :is-active)])}
        [:span.accordion__heading-text "Job Details"]
        [:span.accordion__heading-icon-wrapper
         [:i.accordion__heading-icon.js-accordion-icon
          {:class (u/bem :icon [:block :default :is-dark (if details-open? :minus :plus)])}]]]]
      (when details-open?
        [:div
         {:class (u/bem :accordion__content-wrapper [(when details-open? :is-open)])}
         [:div.accordion__content
          {:class (u/bem :flex-grid-group [])}
          [:div.flex-grid-content
           (job-config-content job-config)
           [:hr.u-margin-bottom-20]
           (description-content app-atom job-name job-config)
           [:hr.u-margin-bottom-20]
           (stats-content app-atom summary scheduled-from scheduled-to)]]])]

     (logs-section app-atom logs)

     [:hr]
     (trend-section trend-data)

     (toaster/loading-overlay app-atom :admin.cronscheduler/cron-overview)]))

;; ============================================================================
;; Screen Registration and Transitions
;; ============================================================================

(defmethod flow/render-screen :admin.cronscheduler/overview [app-atom _]
  (cron-job-overview app-atom))

(defmethod t/transition-after-open :admin.cronscheduler/overview [app-atom _ _]
  (dcommon/kill-search-bar)
  (let [process-atom (cmn/process-cursor app-atom)
        screen-params (cmn/screen-params app-atom)
        job-name (:job-name screen-params)]
    ;; Clear previous data to avoid showing stale data when navigating between jobs
    (swap! process-atom dissoc :cron-overview :cron-trend :job-config :consumer-logs :job-name :logs-page :execute-response)
    ;; Initialize accordion as open
    (swap! process-atom assoc :details-open? false)
    (toaster/open-overlay app-atom :admin.cronscheduler/cron-overview)
    (comms/send :admin.cronscheduler/overview
      {:cron/job-name job-name})
    (presence/open app-atom {:context/ident :admin.cronscheduler/overview})))
